import { AfterViewInit, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { delay, lastValueFrom, Observable, of } from 'rxjs';
import { SvcPrinterData, SvcPrinterElement, SvcPrinterOption } from './svc-printer-classes';

@Component({
  selector: 'svc-printer-modal',
  templateUrl: './svc-printer-modal.component.html',
  styleUrls: ['./svc-printer-modal.component.scss'],
})
export class SvcPrinterModalComponent implements OnInit, AfterViewInit, OnDestroy {
  private _someOptionSeleted = false;
  
  protected get someOptionSeleted(): boolean {
    return this._someOptionSeleted;
  }
  
  protected set someOptionSeleted(value: boolean) {
    if (this._someOptionSeleted !== value) {
      this._someOptionSeleted = value;
      // Force change detection when value changes
      this._cdr?.markForCheck();
    }
  }
  
  protected printInLandspace = false;
  protected isLoading = false;

  private set _isLoading(value: boolean) {
    this.isLoading = value;
    this._dialogRef.disableClose = this.isLoading;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) protected data: SvcPrinterData,
    private _dialogRef: MatDialogRef<SvcPrinterModalComponent>,
    private _cdr: ChangeDetectorRef
  ) {
    // Initialize immediately in constructor before Angular lifecycle hooks
    this.checktIfThereIsSomeOptionSeleted();
  }

  public ngOnInit(): void {
    this.checktIfThereIsSomeOptionSeleted();
  }

  public ngAfterViewInit(): void {
    // Ensure state is computed after view initialization
    this.checktIfThereIsSomeOptionSeleted();
    this._cdr.detectChanges();
  }

  protected async print(): Promise<void> {
    try {
      this._isLoading = true;
      await this._executePrintLifecycle('printWillStart');
      await this._takeBreak();

      const elementsToPrint: HTMLElement[] = [];
      // Defense-in-depth (AB#69626): sources can host stateful widgets (media carousels,
      // rich text editors) whose live layout/scroll state isn't captured by cloneNode(true).
      // The print stylesheet already hides anything marked `print:hidden` and reveals its
      // static replacement, but that relies on the print-media cascade winning — which has
      // been observed to fail for real users despite the classes being correct. Removing
      // `print:hidden` subtrees from the clone outright guarantees they can never render,
      // regardless of any CSS cascade/specificity edge case.
      const stripPrintHiddenDescendants = (element: HTMLElement): void => {
        element.querySelectorAll('.print\\:hidden').forEach((hidden) => hidden.remove());
      };
      const handlerElement = (element: HTMLElement): HTMLElement | null => {
        if (element.classList.contains('hidden')) {
          element.classList.remove('hidden');
        }
        // A detached clone (cloneNode result) has no parentNode, so element.remove() is a
        // no-op here — the caller must skip appending a top-level print:hidden node itself.
        if (element.classList.contains('print:hidden')) return null;
        stripPrintHiddenDescendants(element);
        if (element instanceof HTMLImageElement) {
          element.classList.add('w-full', 'h-full', 'object-contain');
          element.style.maxHeight = '100dvh';
        }
        return element;
      };
      const areAllImages = (elements: (HTMLElement | SvcPrinterElement)[]): boolean => elements.every((e) => (e instanceof Element ? e : e.element) instanceof HTMLImageElement);
      const createEmptyDiv = (): HTMLElement => document.createElement('div');
      const applySeparation = (element: HTMLElement): HTMLElement => {
        element?.classList.add('page-break');
        return element;
      };
      const needsToSeperatePage = (option: SvcPrinterOption): boolean => option.separatedPage && hasSomeElement();
      const hasSomeElement = (): boolean => elementsToPrint.length > 0;
      // Guards against the same source node being cloned into the printout more than once when it is
      // (accidentally or intentionally) referenced by more than one option/sub-option.
      const alreadyIncludedSources = new Set<HTMLElement>();
      const skipAlreadyIncluded = (source: HTMLElement): boolean => {
        if (alreadyIncludedSources.has(source)) return true;
        alreadyIncludedSources.add(source);
        return false;
      };
      const processOptions = (options: SvcPrinterOption[]): void => {
        for (const opt of options ?? []) {
          if ((!(opt.show ?? true) || opt.selected) && (opt.elements?.length || opt.options?.length)) {
            const elements = opt.elements?.filter((el) => el instanceof Element || el?.selected) ?? [];
            const subOptions = opt.options?.filter((opt) => (!(opt.show ?? true) || opt.selected) && opt.elements.length > 0) ?? [];

            if (!elements.length && !subOptions.length) continue;

            if (elements.length) {
              const containerElement = createEmptyDiv();
              if (needsToSeperatePage(opt)) applySeparation(containerElement);
              for (const el of elements) {
                const source = el instanceof Element ? el : el.element;
                if (skipAlreadyIncluded(source)) continue;
                const clonedElement = handlerElement(source.cloneNode(true) as HTMLElement);
                if (clonedElement) containerElement.appendChild(clonedElement);
              }
              if (containerElement.hasChildNodes()) elementsToPrint.push(containerElement);
            }

            for (const subOpt of subOptions) {
              const containerElement = createEmptyDiv();
              if (needsToSeperatePage(subOpt)) applySeparation(containerElement);
              for (const element of subOpt.elements) {
                if (skipAlreadyIncluded(element)) continue;
                const clonedElement = handlerElement(element.cloneNode(true) as HTMLElement);
                if (clonedElement) containerElement.appendChild(clonedElement);
              }
              if (containerElement.hasChildNodes()) elementsToPrint.push(containerElement);
            }
          }
        }
      };

      processOptions(this.data.options);
      processOptions(this.data.additional?.options);

      const containerElement = this._showElementsToRender(elementsToPrint);
      const medias = Array.from<HTMLImageElement | HTMLVideoElement>(containerElement.querySelectorAll('img,video'));
      if (medias.length > 0) {
        await this._waitImgsAndVideosBeLoaded(Array.from(medias));
      }

      await this._executePrintLifecycle('printDidStart');

      await this._takeBreak();

      window.print();

      await this._takeBreak(500);

      await this._executePrintLifecycle('printWillFinish');
      await this._takeBreak();
      this._hideElementsToRender();
      await this._executePrintLifecycle('printDidFinish');
    } finally {
      this._isLoading = false;
    }
  }

  protected changeOptionSelected(option: SvcPrinterOption, fromAdditional: boolean = false): void {
    option.selected = !(option.selected ?? false);
    if (!option.selected && option.options?.length) {
      option.options.forEach((op) => (op.selected = false));
    }
    this.checktIfThereIsSomeOptionSeleted();
    this._cdr.markForCheck();
  }

  protected checktIfThereIsSomeOptionSeleted(): void {
    this.someOptionSeleted = (this.data?.options ?? []).some((x) => x.selected) || (this.data?.additional?.options ?? []).some((x) => x.selected);
  }

  // Stashed inline `display` values (AB#69626) for every other document.body child, so they can
  // be forced hidden and then restored exactly as they were. Inline `!important` beats any
  // stylesheet rule (including Tailwind's forced `!important` utilities and the CDK overlay's
  // own styles), so this guarantees the live dialog/overlay can never bleed into the print
  // output, independent of whether the print.scss cascade correctly hides it.
  private readonly _hiddenSiblingsPreviousDisplay = new Map<HTMLElement, string>();

  private _showElementsToRender(elements: HTMLElement[]): HTMLElement {
    const printContainer = document.createElement('div');
    printContainer.id = 'printable';
    printContainer.classList.add('printable-container');
    for (const element of elements) {
      printContainer.appendChild(element);
    }
    document.body.prepend(printContainer);
    this._hideOtherBodyChildren(printContainer);
    return printContainer;
  }

  private _hideOtherBodyChildren(printContainer: HTMLElement): void {
    Array.from(document.body.children).forEach((child) => {
      if (child === printContainer || !(child instanceof HTMLElement)) return;
      this._hiddenSiblingsPreviousDisplay.set(child, child.style.display);
      child.style.setProperty('display', 'none', 'important');
    });
  }

  private _restoreOtherBodyChildren(): void {
    this._hiddenSiblingsPreviousDisplay.forEach((previousDisplay, child) => {
      if (previousDisplay) {
        child.style.display = previousDisplay;
      } else {
        child.style.removeProperty('display');
      }
    });
    this._hiddenSiblingsPreviousDisplay.clear();
  }

  private _hideElementsToRender(): void {
    this._restoreOtherBodyChildren();
    const printContainer = document.querySelector('#printable');
    if (printContainer) {
      document.body.removeChild(printContainer);
    }
  }

  private _waitImgsAndVideosBeLoaded(medias: (HTMLImageElement | HTMLVideoElement)[]): Promise<void> {
    return new Promise<void>((resolve) => {
      const mediasLoading = Array.from(medias).map(() => true);
      const checkIfLoadingWasDone = (): void => {
        if (mediasLoading.every((loading) => !loading)) {
          resolve();
        }
      };
      medias.forEach((media, i) => {
        const isAlreadyLoaded =
          media instanceof HTMLImageElement
            ? media.complete
            : media.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA;

        if (isAlreadyLoaded) {
          mediasLoading[i] = false;
          checkIfLoadingWasDone();
          return;
        }

        media[media instanceof HTMLImageElement ? 'onload' : 'onloadeddata'] = (): void => {
          mediasLoading[i] = false;
          checkIfLoadingWasDone();
        };
        media.onerror = (): void => {
          mediasLoading[i] = false;
          checkIfLoadingWasDone();
        };
      });
    });
  }

  private async _takeBreak(time = 0): Promise<unknown> {
    return lastValueFrom(of(true).pipe(delay(time)));
  }

  private async _executePrintLifecycle(method: 'printWillStart' | 'printDidStart' | 'printWillFinish' | 'printDidFinish'): Promise<void> {
    const printDidStart = this.data?.onPrinting?.[method]?.(this.data.options);
    if (printDidStart instanceof Observable) await lastValueFrom(printDidStart);
  }

  ngOnDestroy(): void {
    this._hideElementsToRender();
  }
}

